import { useState, useMemo } from 'react';
import {
  Copy,
  Check,
  X,
  Loader2,
  AlertTriangle,
  Hash,
  FileText,
  Link2,
  ChevronDown,
  ChevronUp,
  Merge,
  Eye,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { detectDuplicates } from '@/lib/api';
import { useResolveDuplicate } from '@/features/sources/hooks/useSources';
import { useAuthStore } from '@/store/authStore';
import type { DuplicateMatch, DeduplicationResult, SourceMeta } from '@/features/sources/types';

interface DeduplicatePanelProps {
  folderId: string | null;
  onResolved?: () => void;
}

type ResolutionAction = 'keep_original' | 'keep_duplicate' | 'dismiss';

interface MatchWithExpanded extends DuplicateMatch {
  expanded: boolean;
  resolved: boolean;
  resolution: ResolutionAction | null;
}

function matchTypeIcon(matchType: DuplicateMatch['match_type']) {
  switch (matchType) {
    case 'hash':
      return <Hash className="h-3.5 w-3.5" />;
    case 'doi':
      return <Link2 className="h-3.5 w-3.5" />;
    case 'title_fuzzy':
      return <FileText className="h-3.5 w-3.5" />;
  }
}

function matchTypeLabel(matchType: DuplicateMatch['match_type']) {
  switch (matchType) {
    case 'hash':
      return 'Exact content match';
    case 'doi':
      return 'Same DOI';
    case 'title_fuzzy':
      return 'Similar title';
  }
}

function confidenceColor(confidence: number): string {
  if (confidence >= 0.9) return 'text-rose-600 bg-rose-50 border-rose-200';
  if (confidence >= 0.7) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-slate-600 bg-slate-50 border-slate-200';
}

function confidenceLabel(confidence: number): string {
  if (confidence >= 0.9) return 'High';
  if (confidence >= 0.7) return 'Medium';
  return 'Low';
}

function fieldsMatch(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) return JSON.stringify(a) === JSON.stringify(b);
  return String(a) === String(b);
}

function MetaField({
  label,
  value,
  highlight,
  matchField,
}: {
  label: string;
  value: React.ReactNode;
  highlight: boolean;
  matchField?: boolean;
}) {
  return (
    <div
      className={`px-2 py-1.5 rounded ${
        matchField
          ? 'bg-emerald-50 ring-1 ring-emerald-200'
          : highlight
            ? 'bg-amber-50'
            : ''
      }`}
    >
      <div className="flex items-center gap-1.5">
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
        {matchField && (
          <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-emerald-300 text-emerald-700 bg-emerald-50">
            MATCHED
          </Badge>
        )}
      </div>
      <div className="text-sm text-slate-700 mt-0.5">{value || <span className="text-slate-300">&mdash;</span>}</div>
    </div>
  );
}

function MetaColumn({
  label,
  title,
  meta,
  otherMeta,
  matchType,
}: {
  label: string;
  title: string | null;
  meta?: SourceMeta;
  otherMeta?: SourceMeta;
  matchType: DuplicateMatch['match_type'];
}) {
  const [showFullAbstract, setShowFullAbstract] = useState(false);
  const abstractText = meta?.abstract ?? '';
  const truncated = abstractText.length > 300 && !showFullAbstract;

  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-1">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>

      <MetaField
        label="Title"
        value={<span className="font-medium text-slate-900 break-words">{title ?? 'Untitled'}</span>}
        highlight={false}
        matchField={matchType === 'title_fuzzy'}
      />

      <MetaField
        label="Authors"
        value={meta?.authors?.length ? meta.authors.join(', ') : null}
        highlight={!fieldsMatch(meta?.authors, otherMeta?.authors)}
      />

      <MetaField
        label="Year / Journal"
        value={
          (meta?.year || meta?.journal) ? (
            <span>{meta.year ?? '—'} &middot; {meta.journal ?? '—'}</span>
          ) : null
        }
        highlight={!fieldsMatch(meta?.year, otherMeta?.year) || !fieldsMatch(meta?.journal, otherMeta?.journal)}
      />

      <MetaField
        label="DOI"
        value={
          meta?.doi ? (
            <a
              href={`https://doi.org/${meta.doi}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:underline break-all"
            >
              {meta.doi}
            </a>
          ) : null
        }
        highlight={!fieldsMatch(meta?.doi, otherMeta?.doi)}
        matchField={matchType === 'doi'}
      />

      <MetaField
        label="Abstract"
        value={
          abstractText ? (
            <div>
              <p className="whitespace-pre-line break-words">
                {truncated ? abstractText.slice(0, 300) + '…' : abstractText}
              </p>
              {abstractText.length > 300 && (
                <button
                  type="button"
                  className="mt-1 text-xs text-indigo-600 hover:underline"
                  onClick={() => setShowFullAbstract((v) => !v)}
                >
                  {showFullAbstract ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          ) : null
        }
        highlight={!fieldsMatch(meta?.abstract, otherMeta?.abstract)}
      />

      {(meta?.keywords?.length ?? 0) > 0 && (
        <MetaField
          label="Keywords"
          value={
            <div className="flex flex-wrap gap-1 mt-0.5">
              {meta!.keywords!.map((kw) => (
                <Badge key={kw} variant="secondary" className="text-[11px] px-1.5 py-0">
                  {kw}
                </Badge>
              ))}
            </div>
          }
          highlight={!fieldsMatch(meta?.keywords, otherMeta?.keywords)}
        />
      )}
    </div>
  );
}

export function DeduplicatePanel({ folderId, onResolved }: DeduplicatePanelProps) {
  const user = useAuthStore((s) => s.user);
  const resolveDuplicate = useResolveDuplicate();
  const [status, setStatus] = useState<'idle' | 'scanning' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<DeduplicationResult | null>(null);
  const [matches, setMatches] = useState<MatchWithExpanded[]>([]);
  const [scanProgress, setScanProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);

  const unresolvedCount = useMemo(
    () => matches.filter((m) => !m.resolved).length,
    [matches]
  );

  const resolvedCount = useMemo(
    () => matches.filter((m) => m.resolved).length,
    [matches]
  );

  const handleScan = async () => {
    if (!user) return;

    setStatus('scanning');
    setError(null);
    setScanProgress(0);
    setMatches([]);

    try {
      setScanProgress(30);

      const data = await detectDuplicates({ folder_id: folderId || undefined });

      setScanProgress(100);

      const dedupResult = data as DeduplicationResult;

      setResult(dedupResult);
      setMatches(
        dedupResult.matches.map((m) => ({
          ...m,
          expanded: false,
          resolved: false,
          resolution: null,
        }))
      );
      setStatus('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to scan for duplicates');
      setStatus('error');
    }
  };

  const handleResolve = async (
    matchIndex: number,
    action: ResolutionAction
  ) => {
    const match = matches[matchIndex];
    if (!match) return;

    const deleteId =
      action === 'keep_original'
        ? match.duplicate_id
        : action === 'keep_duplicate'
          ? match.source_id
          : null;

    const keepId =
      action === 'keep_original' ? match.source_id : match.duplicate_id;

    setResolving(match.source_id + match.duplicate_id);

    try {
      await resolveDuplicate.mutateAsync({ deleteId, keepId, action });

      setMatches((prev) =>
        prev.map((m, i) =>
          i === matchIndex ? { ...m, resolved: true, resolution: action } : m
        )
      );

      onResolved?.();
    } catch {
      // Error toast is handled by the mutation's onError
    } finally {
      setResolving(null);
    }
  };

  const toggleExpand = (index: number) => {
    setMatches((prev) =>
      prev.map((m, i) =>
        i === index ? { ...m, expanded: !m.expanded } : m
      )
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Copy className="h-5 w-5 text-indigo-500" />
              Duplicate Detection
            </CardTitle>
            <CardDescription>
              Scan your sources to find and resolve duplicate documents.
              {folderId ? ' Scanning current folder only.' : ' Scanning all sources.'}
            </CardDescription>
          </div>
          <Button
            onClick={handleScan}
            disabled={status === 'scanning'}
            size="sm"
          >
            {status === 'scanning' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Scanning…
              </>
            ) : (
              <>
                <Eye className="mr-2 h-4 w-4" />
                {status === 'done' ? 'Re-scan' : 'Scan Now'}
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Scanning progress */}
        {status === 'scanning' && (
          <div className="space-y-2">
            <Progress value={scanProgress} className="h-2" />
            <p className="text-xs text-slate-500">
              {scanProgress < 50
                ? 'Checking content hashes…'
                : scanProgress < 75
                  ? 'Checking DOIs…'
                  : scanProgress < 100
                    ? 'Comparing titles…'
                    : 'Finalizing…'}
            </p>
          </div>
        )}

        {/* Error state */}
        {status === 'error' && error && (
          <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Results summary */}
        {status === 'done' && result && (
          <>
            <div className="flex items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-center">
                <p className="text-lg font-bold text-slate-900">{result.total_checked}</p>
                <p className="text-xs text-slate-500">Checked</p>
              </div>
              <Separator orientation="vertical" className="h-8" />
              <div className="text-center">
                <p className="text-lg font-bold text-amber-600">{result.duplicates_found}</p>
                <p className="text-xs text-slate-500">Duplicates</p>
              </div>
              <Separator orientation="vertical" className="h-8" />
              <div className="text-center">
                <p className="text-lg font-bold text-emerald-600">{resolvedCount}</p>
                <p className="text-xs text-slate-500">Resolved</p>
              </div>
              {unresolvedCount > 0 && (
                <>
                  <Separator orientation="vertical" className="h-8" />
                  <div className="text-center">
                    <p className="text-lg font-bold text-rose-600">{unresolvedCount}</p>
                    <p className="text-xs text-slate-500">Remaining</p>
                  </div>
                </>
              )}
            </div>

            {/* No duplicates found */}
            {matches.length === 0 && (
              <div className="flex flex-col items-center py-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                  <Check className="h-6 w-6 text-emerald-600" />
                </div>
                <p className="mt-3 text-sm font-medium text-slate-900">No duplicates found</p>
                <p className="mt-1 text-sm text-slate-500">All your sources are unique.</p>
              </div>
            )}

            {/* Duplicate list */}
            {matches.length > 0 && (
              <div className="space-y-3">
                {matches.map((match, index) => (
                  <div
                    key={match.source_id + match.duplicate_id}
                    className={`rounded-lg border ${
                      match.resolved
                        ? 'border-slate-200 bg-slate-50 opacity-60'
                        : 'border-slate-200 bg-white'
                    }`}
                  >
                    {/* Match header */}
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-4 py-3 text-left"
                      onClick={() => toggleExpand(index)}
                    >
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              variant="outline"
                              className={`shrink-0 ${confidenceColor(match.confidence)}`}
                            >
                              {matchTypeIcon(match.match_type)}
                              <span className="ml-1">
                                {Math.round(match.confidence * 100)}%
                              </span>
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{matchTypeLabel(match.match_type)}</p>
                            <p className="text-xs text-slate-400">
                              Confidence: {confidenceLabel(match.confidence)}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {match.source_title ?? 'Untitled'}
                        </p>
                        <p className="truncate text-xs text-slate-500">
                          <span className="font-medium text-indigo-600">{matchTypeLabel(match.match_type)}</span>
                          {' · '}
                          {match.duplicate_title ?? 'Untitled'}
                        </p>
                      </div>

                      {match.resolved && match.resolution && (
                        <Badge variant="secondary" className="shrink-0">
                          <Check className="mr-1 h-3 w-3" />
                          {match.resolution === 'dismiss' ? 'Dismissed' : 'Resolved'}
                        </Badge>
                      )}

                      {match.expanded ? (
                        <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                      )}
                    </button>

                    {/* Expanded details + actions */}
                    {match.expanded && !match.resolved && (
                      <div className="border-t border-slate-200 px-4 py-3">
                        {/* Match reason banner */}
                        <div className="mb-3 flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
                          {matchTypeIcon(match.match_type)}
                          <span className="font-medium">
                            {match.match_type === 'hash' && 'Exact content match — both sources have identical content hash.'}
                            {match.match_type === 'doi' && (
                              <>Same DOI: <span className="font-mono text-xs">{match.source_meta?.doi ?? match.duplicate_meta?.doi ?? '—'}</span></>
                            )}
                            {match.match_type === 'title_fuzzy' && (
                              <>Title similarity: {Math.round(match.confidence * 100)}% (Dice coefficient)</>
                            )}
                          </span>
                        </div>

                        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <MetaColumn
                            label="Original"
                            title={match.source_title}
                            meta={match.source_meta}
                            otherMeta={match.duplicate_meta}
                            matchType={match.match_type}
                          />
                          <MetaColumn
                            label="Duplicate"
                            title={match.duplicate_title}
                            meta={match.duplicate_meta}
                            otherMeta={match.source_meta}
                            matchType={match.match_type}
                          />
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleResolve(index, 'keep_original')}
                            disabled={resolving !== null}
                          >
                            {resolving === match.source_id + match.duplicate_id ? (
                              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Check className="mr-2 h-3.5 w-3.5" />
                            )}
                            Keep Original
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResolve(index, 'keep_duplicate')}
                            disabled={resolving !== null}
                          >
                            <Merge className="mr-2 h-3.5 w-3.5" />
                            Keep Duplicate
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResolve(index, 'dismiss')}
                            disabled={resolving !== null}
                          >
                            <X className="mr-2 h-3.5 w-3.5" />
                            Not a Duplicate
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Idle state */}
        {status === 'idle' && (
          <div className="flex flex-col items-center py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100">
              <Copy className="h-6 w-6 text-indigo-500" />
            </div>
            <p className="mt-3 text-sm text-slate-500">
              Click <span className="font-medium text-slate-700">Scan Now</span> to check for
              duplicate sources using content hashes, DOIs, and title similarity.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

